/**
 * Демо-данные дизайн-пакета (design_handoff_app_v2/src/data.js) на формах
 * реального API (src/api/types). Живут только в mock-адаптере.
 */
import type {
  CanvasConnection,
  CanvasNode,
  CanvasSummary,
  CategoryOption,
  CategoryRef,
  FilterOptionsResponse,
  NamedOption,
  TransitionTypeRef,
  TutorialCode,
  VideoReference,
} from '@/api/types';

const M = `${import.meta.env.BASE_URL}media/`;
const NOW = '2026-09-07T12:00:00Z';

// ——— Справочники (GET /filter-options) ———
const categoryTree: Array<[string, string[]]> = [
  ['Commercial', ['Product', 'Fashion', 'Automotive']],
  ['Food & drink', []],
  ['Wedding', ['Event']],
  ['Documentary', ['Social']],
  ['Real estate', ['Interior']],
  ['Music video', []],
  ['Abstract', []],
];

const slug = (name: string) =>
  name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');

let categoryId = 1;
export const mockCategories: CategoryOption[] = categoryTree.map(([parent, children]) => {
  const id = categoryId++;
  return {
    id,
    name: parent,
    slug: slug(parent),
    parent_id: null,
    children: children.map((child) => ({
      id: categoryId++,
      name: child,
      slug: slug(child),
      parent_id: id,
      children: [],
    })),
  };
});

const categoryByName = new Map<string, CategoryRef>();
for (const parent of mockCategories) {
  categoryByName.set(parent.name, {
    id: parent.id,
    name: parent.name,
    slug: parent.slug,
    parent_id: null,
  });
  for (const child of parent.children)
    categoryByName.set(child.name, {
      id: child.id,
      name: child.name,
      slug: child.slug,
      parent_id: parent.id,
    });
}

const named = (names: string[]): NamedOption[] => names.map((name, i) => ({ id: i + 1, name }));
export const mockTransitionTypes = named([
  'Match cut',
  'Speed ramp',
  'Whip pan',
  'Zoom transition',
  'Cross dissolve',
  'Jump cut',
  'Morph cut',
  'Light leak',
]);
export const mockHooks = named([
  'Reveal',
  'Macro texture',
  'Emotion',
  'Pattern break',
  'Rhythm',
  'Process',
]);

export const mockFilterOptions: FilterOptionsResponse = {
  data: { categories: mockCategories, hooks: mockHooks, transition_types: mockTransitionTypes },
};

const transitionByName = new Map(mockTransitionTypes.map((t) => [t.name, t]));
const hookByName = new Map(mockHooks.map((h) => [h.name, h]));

// ——— Видео ———
type Seed = {
  id: number;
  title: string;
  img: string;
  dur: string;
  cat: string[];
  pacing: NonNullable<VideoReference['pacing']>;
  level: NonNullable<VideoReference['production_level']>;
  hook: string;
  effects: string[];
  features: string[];
  orient: NonNullable<VideoReference['orientation']>;
  tutorials: TutorialCode[];
  media: boolean;
  saved: number[];
};

const seeds: Seed[] = [
  {
    id: 1,
    title: 'Coffee pour, top-down',
    img: 'f1',
    dur: '0:24',
    cat: ['Food & drink', 'Product'],
    pacing: 'slow',
    level: 'mid',
    hook: 'Macro texture',
    effects: ['Match cut', 'Speed ramp'],
    features: ['Sound design'],
    orient: 'horizontal',
    tutorials: ['PP', 'DR'],
    media: true,
    saved: [1],
  },
  {
    id: 2,
    title: 'Sneaker studio spin',
    img: 'f2',
    dur: '0:15',
    cat: ['Product', 'Commercial'],
    pacing: 'fast',
    level: 'high',
    hook: 'Reveal',
    effects: ['Whip pan', 'Zoom transition'],
    features: ['3D', 'Motion graphics'],
    orient: 'vertical',
    tutorials: ['FC'],
    media: true,
    saved: [],
  },
  {
    id: 3,
    title: 'Wedding rooftop, golden hour',
    img: 'f3',
    dur: '1:02',
    cat: ['Wedding', 'Event'],
    pacing: 'slow',
    level: 'mid',
    hook: 'Emotion',
    effects: ['Cross dissolve'],
    features: [],
    orient: 'horizontal',
    tutorials: [],
    media: false,
    saved: [],
  },
  {
    id: 4,
    title: 'Fashion lookbook, studio',
    img: 'f4',
    dur: '0:38',
    cat: ['Fashion', 'Commercial'],
    pacing: 'mixed',
    level: 'high',
    hook: 'Pattern break',
    effects: ['Jump cut', 'Match cut'],
    features: ['Typography'],
    orient: 'vertical',
    tutorials: ['PP', 'CC'],
    media: true,
    saved: [],
  },
  {
    id: 5,
    title: 'Paint swirl macro',
    img: 'f5',
    dur: '0:19',
    cat: ['Abstract', 'Product'],
    pacing: 'slow',
    level: 'low',
    hook: 'Macro texture',
    effects: ['Morph cut'],
    features: ['Visual effects'],
    orient: 'horizontal',
    tutorials: ['DR'],
    media: true,
    saved: [1],
  },
  {
    id: 6,
    title: 'Night drive, neon',
    img: 'f6',
    dur: '0:47',
    cat: ['Automotive', 'Music video'],
    pacing: 'fast',
    level: 'high',
    hook: 'Rhythm',
    effects: ['Speed ramp', 'Light leak'],
    features: ['Sound design', 'Visual effects'],
    orient: 'horizontal',
    tutorials: ['PP'],
    media: true,
    saved: [],
  },
  {
    id: 7,
    title: 'Kitchen prep, handheld',
    img: 'f7',
    dur: '0:31',
    cat: ['Food & drink', 'Documentary'],
    pacing: 'mixed',
    level: 'mid',
    hook: 'Process',
    effects: ['Jump cut'],
    features: [],
    orient: 'horizontal',
    tutorials: ['FC'],
    media: false,
    saved: [],
  },
  {
    id: 8,
    title: 'Loft walkthrough',
    img: 'f8',
    dur: '0:52',
    cat: ['Real estate', 'Interior'],
    pacing: 'slow',
    level: 'mid',
    hook: 'Reveal',
    effects: ['Match cut', 'Cross dissolve'],
    features: ['Typography'],
    orient: 'horizontal',
    tutorials: ['DR', 'PP'],
    media: true,
    saved: [2],
  },
  {
    id: 9,
    title: 'Espresso bar, portrait',
    img: 'f9',
    dur: '0:12',
    cat: ['Food & drink', 'Social'],
    pacing: 'fast',
    level: 'low',
    hook: 'Pattern break',
    effects: ['Whip pan'],
    features: ['AI'],
    orient: 'vertical',
    tutorials: [],
    media: true,
    saved: [],
  },
  {
    id: 10,
    title: 'Ceremony light',
    img: 'f10',
    dur: '0:44',
    cat: ['Wedding'],
    pacing: 'slow',
    level: 'high',
    hook: 'Emotion',
    effects: ['Cross dissolve', 'Light leak'],
    features: ['Sound design'],
    orient: 'horizontal',
    tutorials: ['CC'],
    media: true,
    saved: [],
  },
  {
    id: 11,
    title: 'Sneaker rotation, 3D',
    img: 'f11',
    dur: '0:09',
    cat: ['Product', 'Commercial'],
    pacing: 'fast',
    level: 'high',
    hook: 'Reveal',
    effects: ['Zoom transition'],
    features: ['3D', 'Animations'],
    orient: 'vertical',
    tutorials: ['FC', 'PP'],
    media: true,
    saved: [],
  },
  {
    id: 12,
    title: 'Color splash',
    img: 'f12',
    dur: '0:21',
    cat: ['Abstract', 'Music video'],
    pacing: 'fast',
    level: 'mid',
    hook: 'Rhythm',
    effects: ['Speed ramp', 'Morph cut'],
    features: ['Visual effects', 'Motion graphics'],
    orient: 'horizontal',
    tutorials: ['DR'],
    media: true,
    saved: [],
  },
  {
    id: 13,
    title: 'Studio breakdown',
    img: 'ref-01',
    dur: '0:36',
    cat: ['Commercial'],
    pacing: 'mixed',
    level: 'high',
    hook: 'Reveal',
    effects: ['Match cut'],
    features: ['Motion graphics'],
    orient: 'horizontal',
    tutorials: ['PP'],
    media: true,
    saved: [],
  },
  {
    id: 14,
    title: 'Product on turntable',
    img: 'ref-02',
    dur: '0:18',
    cat: ['Product'],
    pacing: 'slow',
    level: 'mid',
    hook: 'Macro texture',
    effects: ['Cross dissolve'],
    features: [],
    orient: 'horizontal',
    tutorials: [],
    media: false,
    saved: [],
  },
  {
    id: 15,
    title: 'Street portrait',
    img: 'ref-03',
    dur: '0:27',
    cat: ['Documentary', 'Social'],
    pacing: 'mixed',
    level: 'low',
    hook: 'Emotion',
    effects: ['Jump cut'],
    features: ['Typography'],
    orient: 'vertical',
    tutorials: ['FC'],
    media: true,
    saved: [],
  },
  {
    id: 16,
    title: 'Interior dolly',
    img: 'ref-04',
    dur: '0:40',
    cat: ['Interior', 'Real estate'],
    pacing: 'slow',
    level: 'mid',
    hook: 'Reveal',
    effects: ['Match cut'],
    features: [],
    orient: 'horizontal',
    tutorials: ['DR'],
    media: true,
    saved: [],
  },
  {
    id: 17,
    title: 'Beat-synced montage',
    img: 'ref-05',
    dur: '0:33',
    cat: ['Music video'],
    pacing: 'fast',
    level: 'high',
    hook: 'Rhythm',
    effects: ['Speed ramp', 'Whip pan'],
    features: ['Sound design'],
    orient: 'horizontal',
    tutorials: ['PP', 'CC'],
    media: true,
    saved: [],
  },
  {
    id: 18,
    title: 'Two-shot dialogue',
    img: 'ref-06',
    dur: '1:10',
    cat: ['Documentary'],
    pacing: 'slow',
    level: 'mid',
    hook: 'Emotion',
    effects: ['Cross dissolve'],
    features: [],
    orient: 'horizontal',
    tutorials: [],
    media: true,
    saved: [],
  },
  {
    id: 19,
    title: 'Brand ident, type',
    img: 'ref-07',
    dur: '0:08',
    cat: ['Commercial', 'Abstract'],
    pacing: 'fast',
    level: 'high',
    hook: 'Pattern break',
    effects: ['Morph cut'],
    features: ['Typography', 'Animations'],
    orient: 'horizontal',
    tutorials: ['FC'],
    media: true,
    saved: [],
  },
  {
    id: 20,
    title: 'Hands-on unboxing',
    img: 'ref-08',
    dur: '0:29',
    cat: ['Product', 'Social'],
    pacing: 'mixed',
    level: 'low',
    hook: 'Process',
    effects: ['Jump cut'],
    features: ['AI'],
    orient: 'vertical',
    tutorials: ['PP'],
    media: true,
    saved: [],
  },
];

function durationSec(label: string): number {
  const [m = '0', s = '0'] = label.split(':');
  return Number(m) * 60 + Number(s);
}

/** Демо-разбор — генератор прототипа VideoDetail.dc.html; на проде приходит public_summary_html. */
function demoSummaryHtml(seed: Seed): string {
  const tempo =
    seed.pacing === 'slow'
      ? 'Long holds let the texture read; '
      : seed.pacing === 'fast'
        ? 'Cuts land on the beat; '
        : 'Tempo shifts between holds and quick cuts; ';
  const effect = (seed.effects[0] ?? 'cut').toLowerCase();
  return `<p>${seed.title}. ${tempo}the ${effect} carries the transition into the next shot. Hook: ${seed.hook.toLowerCase()}. Shot at ${seed.level} production level.</p>`;
}

function toVideo(seed: Seed): VideoReference {
  const img = `${M}${seed.img}.webp`;
  const seconds = durationSec(seed.dur);
  const clip = `${M}ref-${String(((seed.id - 1) % 10) + 1).padStart(2, '0')}.mp4`;
  const has = (f: string) => seed.features.includes(f);
  // Размеры файла: вертикаль 9:16, горизонталь 16:9, длинные (> 40 с) — 21:9, как в прототипе.
  const dims =
    seed.orient === 'vertical' ? [1080, 1920] : seconds > 40 ? [2520, 1080] : [1920, 1080];
  return {
    id: seed.id,
    title: seed.title,
    source_url: 'https://www.youtube.com/watch?v=gl5c1l7X4SU',
    preview_embed: null,
    public_summary: null,
    public_summary_html: demoSummaryHtml(seed),
    details_public:
      seed.id % 3 === 0
        ? 'Shot on a gimbal with a 35mm; grade pushed towards teal in the shadows.'
        : null,
    duration_sec: seconds,
    platform: 'youtube',
    platform_video_id: 'gl5c1l7X4SU',
    pacing: seed.pacing,
    hook_id: hookByName.get(seed.hook)?.id ?? null,
    production_level: seed.level,
    orientation: seed.orient,
    has_visual_effects: has('Visual effects'),
    has_3d: has('3D'),
    has_animations: has('Animations'),
    has_typography: has('Typography'),
    has_sound_design: has('Sound design'),
    has_ai: has('AI'),
    has_motion_graphics: has('Motion graphics'),
    is_saved: seed.saved.length > 0,
    saved_canvas_ids: [...seed.saved],
    categories: seed.cat
      .map((name) => categoryByName.get(name))
      .filter((c): c is CategoryRef => !!c),
    transition_types: seed.effects
      .map((name) => transitionByName.get(name))
      .filter((t): t is TransitionTypeRef => !!t),
    // Как на проде: запись — техника со ссылками по редакторам; вторая техника — только
    // произвольной ссылкой без редактора (блок Other в разделе Learn).
    tutorials: seed.tutorials.length
      ? [
          {
            id: seed.id * 10,
            label: seed.effects[0] ?? seed.title,
            fc_url: seed.tutorials.includes('FC') ? 'https://example.com/tutorial/fc' : null,
            pp_url: seed.tutorials.includes('PP') ? 'https://example.com/tutorial/pp' : null,
            dr_url: seed.tutorials.includes('DR') ? 'https://example.com/tutorial/dr' : null,
            cc_url: seed.tutorials.includes('CC') ? 'https://example.com/tutorial/cc' : null,
            custom_tutorials: null,
          },
          ...(seed.effects[1]
            ? [
                {
                  id: seed.id * 10 + 1,
                  label: seed.effects[1],
                  custom_tutorials: [
                    { name: seed.effects[1], url: 'https://example.com/tutorial/custom' },
                  ],
                },
              ]
            : []),
        ]
      : [],
    hook: hookByName.get(seed.hook) ? { id: hookByName.get(seed.hook)!.id, name: seed.hook } : null,
    extra: { thumbnail_url: img, keyframes: [img] },
    media: seed.media
      ? {
          type: 'video/mp4',
          width: dims[0] ?? null,
          height: dims[1] ?? null,
          duration_sec: seconds,
          bytes: null,
          src: clip,
          poster_url: img,
          attribution: {
            platform: 'youtube',
            source_url: 'https://www.youtube.com/watch?v=gl5c1l7X4SU',
            author_name: null,
            author_handle: null,
            author_url: null,
          },
        }
      : null,
  };
}

const base = seeds.map(toVideo);
const generated: VideoReference[] = [];
for (let i = 0; i < 80; i++) {
  const src = base[i % base.length]!;
  generated.push({
    ...src,
    id: base.length + i + 1,
    title: `${src.title} · take ${Math.floor(i / base.length) + 2}`,
    is_saved: false,
    saved_canvas_ids: [],
    tutorials: src.tutorials.map((t, j) => ({ ...t, id: (base.length + i + 1) * 10 + j })),
  });
}

export const mockVideos: VideoReference[] = [...base, ...generated];

/** Закладки прототипа: видео с канвасами + явные 3 и 6. */
export const mockInitialBookmarks: number[] = [
  ...base.filter((v) => v.saved_canvas_ids.length > 0).map((v) => v.id),
  3,
  6,
];

/** Состав канвасов прототипа (id видео → канвасы) как начальное состояние mock. */
export const mockInitialCanvasVideos: Record<number, number[]> = { 1: [1, 5], 2: [8], 3: [] };

// ——— Канвасы ———
const person = (
  user_id: number,
  name: string,
  initials: string,
  role: CanvasSummary['participants'][number]['role'],
) => ({
  id: user_id,
  user_id,
  name,
  initials,
  role,
  via: 'invite' as const,
  status: 'accepted' as const,
  email: null,
});

export const mockCanvases: CanvasSummary[] = [
  {
    id: 1,
    name: 'Coffee brand — spring spot',
    description: null,
    is_default: true,
    nodes_count: 7,
    cover_url: `${M}f1.webp`,
    participants: [
      person(1, 'Alex Kim', 'AK', 'owner'),
      person(2, 'Maria R.', 'MR', 'editor'),
      person(3, 'Dan L.', 'DL', 'viewer'),
      person(4, 'Nika P.', 'NP', 'viewer'),
    ],
    share: {
      token: 'demo-view-token',
      mode: 'view',
      shared_at: NOW,
      expires_at: '2026-09-30T12:00:00Z',
      status: 'active',
      views_count: 41,
    },
    created_at: NOW,
    updated_at: '2026-09-07T10:00:00Z',
  },
  {
    id: 2,
    name: 'Loft listing walkthrough',
    description: null,
    is_default: false,
    nodes_count: 4,
    cover_url: `${M}f8.webp`,
    participants: [person(1, 'Alex Kim', 'AK', 'owner')],
    share: null,
    created_at: NOW,
    updated_at: '2026-09-06T12:00:00Z',
  },
  {
    id: 3,
    name: 'Moodboard — neon nights',
    description: null,
    is_default: false,
    nodes_count: 12,
    cover_url: `${M}f6.webp`,
    participants: [person(1, 'Alex Kim', 'AK', 'owner'), person(3, 'Dan L.', 'DL', 'editor')],
    share: {
      token: 'demo-edit-token',
      mode: 'edit',
      shared_at: NOW,
      expires_at: null,
      status: 'active',
      views_count: 128,
    },
    created_at: NOW,
    updated_at: '2026-08-30T12:00:00Z',
  },
];

const nodeBase = (id: string, x: number, y: number, width: number, height: number) => ({
  id,
  canvas_id: 1,
  x,
  y,
  width,
  height,
  rotation: 0,
  z_index: 0,
  author_id: 1,
  created_at: NOW,
  updated_at: NOW,
});

export const mockCanvasNodes: CanvasNode[] = [
  {
    ...nodeBase('n1', 120, 140, 300, 200),
    type: 'video',
    video_reference_id: 1,
    source_url: null,
    poster_url: `${M}f1.webp`,
    in_sec: 2.4,
    out_sec: 6.1,
    loop: true,
    muted: true,
    embed_only: false,
  },
  {
    ...nodeBase('n2', 480, 120, 200, 130),
    type: 'frame',
    asset_id: null,
    image_url: `${M}f1.webp`,
    source_node_id: 'n1',
    timestamp_sec: 3.2,
  },
  {
    ...nodeBase('n3', 120, 400, 300, 120),
    type: 'text',
    title: 'Scene 1 — pour',
    body: 'Top-down macro. Hold the crema for two beats, then match cut to the cup on the counter.',
  },
  {
    ...nodeBase('n4', 760, 220, 300, 200),
    type: 'video',
    video_reference_id: 5,
    source_url: null,
    poster_url: `${M}f5.webp`,
    in_sec: 0,
    out_sec: 4.5,
    loop: true,
    muted: true,
    embed_only: false,
  },
  {
    ...nodeBase('n5', 520, 320, 120, 80),
    type: 'asset',
    asset_id: 'camera-dolly-in',
    label: 'Camera · dolly in',
    scale: 1,
    color: null,
  },
  {
    ...nodeBase('n6', 780, 480, 240, 160),
    type: 'image',
    asset_id: null,
    image_url: `${M}ref-06-still-1.webp`,
    file_name: 'lighting-ref.jpg',
    natural_width: null,
    natural_height: null,
  },
  {
    ...nodeBase('n7', 1120, 140, 300, 200),
    type: 'video',
    video_reference_id: 12,
    source_url: null,
    poster_url: `${M}f12.webp`,
    in_sec: 5,
    out_sec: 8,
    loop: true,
    muted: true,
    embed_only: true,
  },
];

export const mockConnections: CanvasConnection[] = [
  {
    id: 'c1',
    canvas_id: 1,
    from_node_id: 'n1',
    to_node_id: 'n2',
    from_side: 'auto',
    to_side: 'auto',
    style: 'dashed',
    arrow: true,
    label: 'frame @ 03.2',
  },
  {
    id: 'c2',
    canvas_id: 1,
    from_node_id: 'n1',
    to_node_id: 'n4',
    from_side: 'auto',
    to_side: 'auto',
    style: 'solid',
    arrow: true,
    label: 'match cut',
  },
  {
    id: 'c3',
    canvas_id: 1,
    from_node_id: 'n4',
    to_node_id: 'n7',
    from_side: 'auto',
    to_side: 'auto',
    style: 'solid',
    arrow: true,
    label: 'speed ramp',
  },
  {
    id: 'c4',
    canvas_id: 1,
    from_node_id: 'n3',
    to_node_id: 'n1',
    from_side: 'auto',
    to_side: 'auto',
    style: 'dashed',
    arrow: true,
    label: '',
  },
];

/** Тарифы: цены как на проде (SubscriptionEnum 9/72); реальные — из /subscription-plans (BILL-6). */
export const mockPlans = [
  {
    id: 1,
    name: 'Monthly',
    slug: 'monthly' as const,
    price: 9,
    currency: 'USD',
    duration_days: 30,
    is_active: true,
  },
  {
    id: 2,
    name: 'Yearly',
    slug: 'yearly' as const,
    price: 72,
    currency: 'USD',
    duration_days: 365,
    is_active: true,
  },
];

/** Пользователь прототипа. */
export const mockUser = {
  id: 1,
  name: 'Alex Kim',
  email: 'alex@studio.com',
};
